import {
  API_QUERY_PARAMS,
  type ChurchAdminAccessItem,
  type ChurchAdminAccessListParams,
  type ChurchAdminAccessListResponse,
  type GrantChurchAdminAccessPayload,
  type InitTempChurchAdminAccessPayload,
  type InitTempChurchAdminAccessResponse,
} from '@/types';
import { ApiService } from './api.service';

const BASE_URL = '/api/v1/admin/church-admin-access';
const INIT_TEMP_ACCESS_URL = '/api/v1/users/current/church-admin-access';
const DEFAULT_LIMIT = 10;

type RawChurchAdminAccessListResponse =
  | ChurchAdminAccessItem[]
  | {
      items?: ChurchAdminAccessItem[];
      total?: number;
      limit?: number;
    };

class ChurchAdminAccessService extends ApiService {
  public async list(params: ChurchAdminAccessListParams = {}): Promise<ChurchAdminAccessListResponse> {
    const queryParams = new URLSearchParams();
    const limit = params.limit ?? DEFAULT_LIMIT;

    if (params.term) queryParams.append(API_QUERY_PARAMS.TERM, params.term);
    if (params.cursor) queryParams.append(API_QUERY_PARAMS.CURSOR, params.cursor);
    queryParams.append(API_QUERY_PARAMS.LIMIT, String(limit));

    const url = `${BASE_URL}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await this.get<RawChurchAdminAccessListResponse>(url);
    const data = response.data;

    if (Array.isArray(data)) {
      return {
        items: data,
        limit,
        total: data.length,
      };
    }

    return {
      items: data.items ?? [],
      limit: data.limit ?? limit,
      total: data.total,
    };
  }

  public async grantChurchAccess({
    churchId,
    userId,
    payload,
  }: {
    churchId: string;
    userId: string;
    payload: GrantChurchAdminAccessPayload;
  }): Promise<void> {
    const url = `${BASE_URL}/churches/${churchId}/admins/${userId}`;
    await this.post<void>(url, payload);
  }

  public async removeChurchAccess({ churchId, userId }: { churchId: string; userId: string }): Promise<void> {
    const url = `${BASE_URL}/churches/${churchId}/admins/${userId}`;
    await this.delete<void>(url);
  }

  public async initTempAccess(payload: InitTempChurchAdminAccessPayload): Promise<InitTempChurchAdminAccessResponse> {
    const response = await this.post<InitTempChurchAdminAccessResponse>(INIT_TEMP_ACCESS_URL, payload);
    return response.data;
  }
}

export const churchAdminAccessService = new ChurchAdminAccessService();
